import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  LinearProgress,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Select,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Inventory as InventoryIcon,
  GridView as GridViewIcon,
  List as ListViewIcon,
  CategoryOutlined as CategoryIcon,
  FolderOpen as FolderOpenIcon,
  FileDownload as FileDownloadIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import Layout from '../../components/layout/Layout';
import ProductForm from '../../components/products/ProductFormEnhanced';
import productService from '../../services/productService';
import purchaseOrderService from '../../services/purchaseOrderService';
import { useSnackbar } from 'notistack';
import categoryService from '../../services/categoryService';
import unitService from '../../services/unitService';
import { getCurrentUser } from '../../services/authService';
import axios from 'axios';
import { getAuthHeader } from '../../services/authService';
import API_CONFIG from '../../config';
import * as XLSX from 'xlsx';

// Mock data as a last resort fallback
const MOCK_PRODUCTS = [
  {
    id: 1,
    name: 'Product 1',
    category: { id: 1, name: 'Category 1' },
    sellingPrice: 19.99,
    quantity: 50,
    unit: { abbreviation: 'Pcs' },
    status: 'ACTIVE'
  },
  {
    id: 2,
    name: 'Product 2',
    category: { id: 2, name: 'Category 2' },
    sellingPrice: 29.99,
    quantity: 30,
    unit: { abbreviation: 'Pcs' },
    status: 'ACTIVE'
  },
  {
    id: 3,
    name: 'Product 3',
    category: { id: 1, name: 'Category 1' },
    sellingPrice: 9.99,
    quantity: 10,
    unit: { abbreviation: 'Pcs' },
    status: 'INACTIVE'
  }
];

const GRID_PAGE_SIZE = 12;
const LIST_PAGE_SIZE = 50;

const LIST_ACTIONS_COL_PX = 72;

/** Equal data columns; Actions fixed width on the right. */
const getListColWidth = (showCategory) => {
  const dataCols = showCategory ? 8 : 7;
  return `calc((100% - ${LIST_ACTIONS_COL_PX}px) / ${dataCols})`;
};

const LIST_TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    py: 0.75,
    px: 1.5,
    fontSize: '0.8125rem',
    borderBottom: '1px solid',
    borderColor: 'divider',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  '& .MuiTableCell-head': {
    py: 1,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'text.secondary',
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableCell-head.col-numeric': {
    textAlign: 'right',
  },
  '& .MuiTableCell-head.col-retailer': {
    textAlign: 'right',
    pr: 2.5,
  },
  '& .MuiTableCell-root.col-numeric': {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  '& .MuiTableCell-root.col-retailer': {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    pr: 2.5,
  },
  '& .MuiTableCell-head.col-retailer': {
    pr: 2.5,
  },
  '& .MuiTableCell-root.col-actions': {
    width: LIST_ACTIONS_COL_PX,
    maxWidth: LIST_ACTIONS_COL_PX,
    minWidth: LIST_ACTIONS_COL_PX,
    pl: 1.5,
    pr: 1,
    textAlign: 'center',
    overflow: 'visible',
    textOverflow: 'clip',
  },
  '& .MuiTableRow-root:hover': {
    bgcolor: 'action.hover',
  },
};

const computeCatalogStats = (items, totalCount) => {
  const list = items || [];
  return {
    totalProducts: totalCount ?? list.length,
    activeProducts: list.filter((p) => String(p.status || 'ACTIVE').toUpperCase() === 'ACTIVE').length,
    totalStock: list.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0),
  };
};

const ProductStatCard = ({ label, value, suffix, icon: Icon, color, bgColor }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      height: '100%',
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: '#fff',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 1.5,
        bgcolor: bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon sx={{ color, fontSize: 26 }} />
    </Box>
    <Box minWidth={0}>
      <Typography variant="body2" color="text.secondary" fontWeight={500} noWrap>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} lineHeight={1.2} sx={{ mt: 0.25 }}>
        {value}
        {suffix && (
          <Typography component="span" variant="body2" color="text.secondary" fontWeight={500} sx={{ ml: 0.5 }}>
            {suffix}
          </Typography>
        )}
      </Typography>
    </Box>
  </Paper>
);

const ProductsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const user = getCurrentUser();
  const companyId = user?.companyId || 1; // Fallback to 1 for development
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'edit', or 'view'
  /** Bumped each time the product dialog opens so the form can reliably focus the first field after the transition. */
  const [productDialogFocusSession, setProductDialogFocusSession] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [activeTabIndex, setActiveTabIndex] = useState(0); // Track active tab
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: LIST_PAGE_SIZE,
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [categoryTree, setCategoryTree] = useState({ rootCategories: [], subcategories: {} });
  const [originalCategories, setOriginalCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]); // For tab navigation
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [categoryDropdownAnchorEl, setCategoryDropdownAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitDialogMessage, setLimitDialogMessage] = useState('');
  const [catalogStats, setCatalogStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    totalStock: 0,
  });

  // Shared data for ProductForm to avoid repeated fetches
  const [allCategoriesForForm, setAllCategoriesForForm] = useState([]);
  const [allUnitsForForm, setAllUnitsForForm] = useState([]);
  const [allTaxesForForm, setAllTaxesForForm] = useState([]);

  // Get the menu state for each product
  const isMenuOpen = Boolean(anchorEl);

  // Fetch all categories, units, and taxes once on mount for ProductForm
  const fetchSharedFormData = useCallback(async () => {
    try {
      console.log('Fetching shared form data (categories, units, taxes)...');

      // Fetch all categories
      const categoriesResponse = await categoryService.getAllCategories();
      if (categoriesResponse?.content) {
        setAllCategoriesForForm(categoriesResponse.content);
        console.log('Loaded categories for form:', categoriesResponse.content.length);
      }

      // Fetch all units
      const unitsResponse = await unitService.getAllUnits();
      if (unitsResponse?.content) {
        setAllUnitsForForm(unitsResponse.content);
        console.log('Loaded units for form:', unitsResponse.content.length);
      }

      // Fetch all taxes
      const taxesData = await productService.getTaxes();
      if (Array.isArray(taxesData)) {
        setAllTaxesForForm(taxesData);
        console.log('Loaded taxes for form:', taxesData.length);
      }
    } catch (error) {
      console.error('Error fetching shared form data:', error);
      // Don't show error notification, forms will fetch their own data as fallback
    }
  }, [companyId]);

  // Fetch main categories for tab navigation
  const fetchMainCategories = useCallback(async () => {
    try {
      const data = await categoryService.getRootCategories(companyId);
      setMainCategories(data);
    } catch (error) {
      console.error('Error fetching main categories:', error);
      enqueueSnackbar('Failed to load category navigation', { variant: 'error' });
    }
  }, [companyId, enqueueSnackbar]);

  // Initial load - fetch shared data and main categories for tabs
  useEffect(() => {
    fetchSharedFormData();
    fetchMainCategories();
  }, [fetchSharedFormData, fetchMainCategories]);

  // Fetch the full category tree once on mount
  const fetchCategoryTree = useCallback(async () => {
    try {
      const allCategories = await categoryService.getCategoriesByCompany(companyId);

      // Build category tree
      const tree = categoryService.buildCategoryTree({
        content: allCategories
      });

      setCategoryTree(tree);
    } catch (error) {
      console.error('Error fetching category tree:', error);
      enqueueSnackbar('Failed to load category tree', { variant: 'error' });
    }
  }, [companyId, enqueueSnackbar]);

  // Fetch categories based on current navigation
  const fetchCategories = useCallback(async () => {
    try {
      let categoryData;

      if (currentCategory) {
        // Fetch subcategories for the current category
        categoryData = await categoryService.getSubcategories(currentCategory.id);
      } else {
        // Fetch root categories (no parent)
        categoryData = await categoryService.getRootCategories(companyId);
      }

      setCategories(categoryData);

      // Store original categories for filtering
      if (!currentCategory) {
        setOriginalCategories(categoryData);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      enqueueSnackbar('Failed to load categories', { variant: 'error' });
    }
  }, [currentCategory, companyId, enqueueSnackbar]);

  // Initial load - fetch category tree once on mount
  useEffect(() => {
    fetchCategoryTree();
  }, [fetchCategoryTree]);

  // Fetch categories when currentCategory changes
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories])

  // Note: Product fetching is handled by fetchTabProducts which is called from handleTabChange and handleNavigateToCategory

  // Apply search filter if needed (Only for categories now)
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();

      // Only filter categories client-side if no category is selected
      if (!currentCategory) {
        if (originalCategories.length > 0) {
          const filtered = originalCategories.filter(category =>
            category.name.toLowerCase().includes(query) ||
            (category.description && category.description.toLowerCase().includes(query))
          );
          setCategories(filtered);
        }
      }
      // --- Removed client-side product filtering logic ---
    } else {
      // Reset filters when search query is empty
      if (currentCategory) {
        // Product list will be reset by fetchTabProducts called in handleSearch
      } else {
        // Use original categories instead of refetching
        setCategories(originalCategories);
      }
    }
  }, [originalCategories, searchQuery, currentCategory]); // Removed 'products' dependency

  // Handle navigating to a category
  const handleNavigateToCategory = (category) => {
    // Set the current category
    setCurrentCategory(category);

    // Reset pagination when navigating
    setPagination(prev => ({
      ...prev,
      page: 0
    }));

    // If navigating to a root category, update the tab index
    if (!currentCategory) { // Only when navigating from root
      const rootIndex = mainCategories.findIndex(cat => cat.id === category.id);
      if (rootIndex !== -1) {
        setActiveTabIndex(rootIndex + 1); // +1 because "ALL" is index 0
      }
    }

    // Always fetch products for this category
    fetchTabProducts(category.id);
  };


  const handleOpenDialog = (mode, product = null) => {
    setProductDialogFocusSession((s) => s + 1);
    setDialogMode(mode);
    setCurrentProduct(product);
    setOpenDialog(true);
  };

  // Fetch full product details before editing/viewing to get complete category/unit data
  const handleEditProduct = async (productId, mode = 'edit') => {
    try {
      const fullProduct = await productService.getProductById(productId);

      // Detect if current user is a location user (same logic as fetchTabProducts)
      let user = JSON.parse(localStorage.getItem('user')) || {};
      let userLocationData = user?.location || null;
      let hasLocation = userLocationData && userLocationData.id;

      if (!hasLocation) {
        try {
          const axiosInstance = (await import('../../utils/axios')).default;
          const response = await axiosInstance.get('/auth/me', {
            headers: getAuthHeader()
          });

          if (response.data && response.data.location) {
            user = response.data;
            userLocationData = response.data.location;
            hasLocation = userLocationData && userLocationData.id;
            localStorage.setItem('user', JSON.stringify(user));
          }
        } catch (fetchError) {
          console.warn('[handleEditProduct] Could not fetch user location from backend:', fetchError);
        }
      }

      // For location users: override quantity with value from location_products table
      if (hasLocation) {
        try {
          const locationProducts = await productService.getLocationProducts();
          const locationProduct = (locationProducts || []).find(lp => lp.productId === productId);

          if (locationProduct) {
            fullProduct.quantity = Number(locationProduct.quantity) || 0;
            console.log(`[handleEditProduct] Using location quantity: ${fullProduct.quantity} for product ${productId}`);
          } else {
            // If product not found in location_products, treat as zero stock at this location
            fullProduct.quantity = 0;
            console.log(`[handleEditProduct] Product ${productId} not found in location_products, setting quantity to 0`);
          }
        } catch (locationError) {
          console.error('[handleEditProduct] Error fetching location product quantity:', locationError);
          // If location lookup fails, keep products table quantity as a fallback
        }
      }

      setDialogMode(mode);
      setCurrentProduct(fullProduct);
      setProductDialogFocusSession((s) => s + 1);
      setOpenDialog(true);
    } catch (error) {
      console.error('Error fetching product details:', error);
      enqueueSnackbar('Failed to load product details', { variant: 'error' });
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentProduct(null);
  };

  const handleMenuOpen = (event, productId) => {
    setAnchorEl(event.currentTarget);
    setSelectedProductId(productId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProductId(null);
  };

  const handlePageChange = (event, value) => {
    setPagination({
      ...pagination,
      page: value - 1 // MUI Pagination is 1-based, but our API is 0-based
    });
  };

  const handleProductSubmit = async (payload) => {
    try {
      const productData = payload?.productData ? payload.productData : payload;
      const productBatches = Array.isArray(payload?.productBatches) ? payload.productBatches : [];
      console.log('Submitting product data:', productData); // Debug log
      if (dialogMode === 'create') {
        const createdProduct = await productService.createProduct(productData);
        if (createdProduct?.id && productBatches.length > 0) {
          for (const batch of productBatches) {
            await purchaseOrderService.createProductBatch({
              productId: createdProduct.id,
              batchNumber: typeof batch.batchNumber === 'string' ? batch.batchNumber.trim() : batch.batchNumber,
              mfd: batch.mfd || null,
              expiryDate: batch.expiryDate || null,
              mrp: batch.mrp,
              purchasePrice: batch.purchasePrice,
              sellingPrice: batch.sellingPrice,
              quantity: batch.quantity ?? 0,
              status: batch.status || 'ACTIVE',
              taxId: productData.taxId || null
            });
          }
        }
        enqueueSnackbar('Product created successfully', { variant: 'success' });
      } else if (dialogMode === 'edit' && currentProduct) {
        console.log('Updating product with ID:', currentProduct.id, 'Data:', productData); // Debug log
        await productService.updateProduct(currentProduct.id, productData);
        if (productBatches.length > 0) {
          for (const batch of productBatches) {
            const commonPayload = {
              productId: currentProduct.id,
              batchNumber: typeof batch.batchNumber === 'string' ? batch.batchNumber.trim() : batch.batchNumber,
              mfd: batch.mfd || null,
              expiryDate: batch.expiryDate || null,
              mrp: batch.mrp,
              purchasePrice: batch.purchasePrice,
              sellingPrice: batch.sellingPrice,
              quantity: batch.quantity ?? 0,
              status: batch.status || 'ACTIVE',
              taxId: productData.taxId || null
            };
            const batchNumericId =
              batch.id != null && batch.id !== ''
                ? Number(batch.id)
                : NaN;
            if (Number.isInteger(batchNumericId) && batchNumericId > 0) {
              await purchaseOrderService.updateProductBatch(batchNumericId, commonPayload);
            } else {
              await purchaseOrderService.createProductBatch(commonPayload);
            }
          }
        }
        enqueueSnackbar('Product updated successfully', { variant: 'success' });
      }

      handleCloseDialog();
      fetchTabProducts();
    } catch (error) {
      console.error('Error submitting product:', error);
      console.error('Error response:', error.response?.data); // Debug log
      const msg = error.response?.data?.message || 'Failed to save product';
      if (/limit|exceed|maximum|reached/i.test(String(msg))) {
        setLimitDialogMessage(String(msg));
        setLimitDialogOpen(true);
      } else {
        enqueueSnackbar('Failed to save product', { variant: 'error' });
      }
    }
  };

  // Memoize the product card render function
  const renderProductCard = useCallback((product) => {
    const imageUrl = product.imageUrl ?
      productService.getProductImageUrl(product.imageUrl) :
      '/placeholder-product.png';

    console.log('Rendering product:', product.name, 'Status:', product.status); // Debug log
    const isInactive = String(product.status || 'ACTIVE').toUpperCase() === 'INACTIVE';

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={product.id}>
        <Card sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s',
          opacity: isInactive ? 0.7 : 1,
          filter: isInactive ? 'grayscale(50%)' : 'none',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4
          },
          maxWidth: '100%',
          position: 'relative',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
        }}>
          <Box sx={{
            position: 'relative',
            paddingTop: '100%', // Square aspect ratio for product images
            width: '100%',
            bgcolor: 'white',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <CardMedia
              component="img"
              image={imageUrl}
              alt={product.name}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                p: 2,
                filter: isInactive ? 'grayscale(50%)' : 'none'
              }}
              loading="lazy"
            />
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, product.id)}
              sx={{
                position: 'absolute',
                top: 5,
                right: 5,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
          <CardContent sx={{ flexGrow: 1, p: 2, pt: 1.5, pb: '16px !important' }}>
            <Typography
              variant="subtitle1"
              component="h2"
              noWrap
              sx={{
                color: isInactive ? 'text.disabled' : 'text.primary',
                fontWeight: 'medium',
                mb: 0.5
              }}
            >
              {product.name}
            </Typography>
            <Typography
              color={isInactive ? 'text.disabled' : 'text.secondary'}
              variant="body2"
              sx={{ mb: 1.5 }}
            >
              {product.category?.name || 'Uncategorized'}
            </Typography>

            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 0.5
            }}>
              <Typography variant="caption" color="text.secondary">
                Sale Price
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Quantity
              </Typography>
            </Box>

            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 0.5
            }}>
              <Typography
                variant="body2"
                fontWeight="bold"
                color={isInactive ? 'text.disabled' : 'primary.main'}
              >
                ₹{parseFloat(product.sellingPrice).toFixed(2)}
              </Typography>
              <Typography
                variant="body2"
                color={isInactive ? 'text.disabled' : 'text.primary'}
              >
                {product.quantity} {product.unit?.abbreviation || 'Pcs'}
              </Typography>
            </Box>

            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Chip
                label={isInactive ? 'Inactive' : 'Active'}
                size="small"
                sx={{
                  height: '22px',
                  fontWeight: 'medium',
                  backgroundColor: isInactive ? '#e0e0e0' : '#e8f5e9',
                  color: isInactive ? '#757575' : '#2e7d32',
                  border: isInactive ? '1px solid #bdbdbd' : '1px solid #a5d6a7',
                  '& .MuiChip-label': {
                    px: 1,
                    py: 0
                  }
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  }, [handleMenuOpen]);

  // Category Card Component (to avoid hooks inside callback)
  const CategoryCard = ({ category }) => {
    const [imageError, setImageError] = useState(false);

    // Get image URL for category with error handling
    const getCategoryImage = () => {
      if (imageError || !category.imageUrl) {
        // Generate a nice placeholder with category initial
        const initial = category.name.substring(0, 2).toUpperCase();
        return `https://via.placeholder.com/400x400/1976D2/FFFFFF?text=${encodeURIComponent(initial)}`;
      }
      return categoryService.getCategoryImageUrl(category.imageUrl);
    };

    // Check if this category has subcategories
    const hasSubcategories = categoryTree.subcategories[category.id]?.length > 0;

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={category.id}>
        <Card
          onClick={() => handleNavigateToCategory(category)}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            '&:hover': {
              transform: 'translateY(-6px)',
              boxShadow: '0 12px 24px rgba(25, 118, 210, 0.2)',
              '& .category-icon-overlay': {
                opacity: 1,
                transform: 'scale(1)'
              }
            },
            border: '2px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
            overflow: 'hidden'
          }}
        >
          {/* Image Section with Hover Effect */}
          <Box sx={{
            position: 'relative',
            paddingTop: '75%', // 4:3 aspect ratio
            width: '100%',
            bgcolor: 'grey.50',
            overflow: 'hidden'
          }}>
            <CardMedia
              component="img"
              image={getCategoryImage()}
              alt={category.name}
              onError={() => setImageError(true)}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s ease'
              }}
              loading="lazy"
            />
            {/* Hover Overlay */}
            <Box
              className="category-icon-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(25, 118, 210, 0.12)',
                opacity: 0,
                transform: 'scale(0.95)',
                transition: 'all 0.3s ease'
              }}
            >
              <ChevronRightIcon sx={{ color: 'primary.main', fontSize: 64, fontWeight: 'bold' }} />
            </Box>
          </Box>

          {/* Content Section */}
          <CardContent sx={{
            flexGrow: 1,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            position: 'relative'
          }}>
            {/* Category Name with Icon */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <CategoryIcon sx={{ color: 'primary.main', fontSize: 22 }} />
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  color: 'text.primary'
                }}
              >
                {category.name}
              </Typography>
            </Box>

            {/* Description */}
            {category.description && (
              <Typography
                color="text.secondary"
                variant="body2"
                sx={{
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  lineHeight: 1.5,
                  fontSize: '0.875rem',
                  minHeight: '2.625em'
                }}
              >
                {category.description}
              </Typography>
            )}

            {/* Subcategories Badge */}
            {hasSubcategories && (
              <Box sx={{ mt: 'auto', pt: 1 }}>
                <Chip
                  icon={<FolderOpenIcon fontSize="small" />}
                  label={`${categoryTree.subcategories[category.id].length} ${categoryTree.subcategories[category.id].length === 1 ? 'Subcategory' : 'Subcategories'}`}
                  size="small"
                  color="primary"
                  sx={{
                    height: '26px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    borderRadius: '13px'
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  // Render category card for displaying categories
  const renderCategoryCard = useCallback((category) => {
    return <CategoryCard key={category.id} category={category} />;
  }, []);

  const formatPrice = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : '—';
  };

  const formatHsn = (value) => {
    const s = value != null ? String(value).trim() : '';
    return s || '—';
  };

  const formatTax = (product) => {
    if (product.tax?.rate != null) return `${Number(product.tax.rate)}%`;
    if (product.taxRate != null) return `${Number(product.taxRate)}%`;
    if (product.tax?.name) return product.tax.name;
    return '—';
  };

  const renderProductListTable = useCallback(
    (productsToRender, { showCategory = true, tableKey = 'product-list' } = {}) => {
      const colW = { width: getListColWidth(showCategory), maxWidth: getListColWidth(showCategory) };

      return (
        <Grid item xs={12} key={tableKey}>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 1, width: '100%' }}>
            <Table size="small" sx={LIST_TABLE_SX}>
              <colgroup>
                <col style={colW} />
                {showCategory && <col style={colW} />}
                <col style={colW} />
                <col style={colW} />
                <col style={colW} />
                <col style={colW} />
                <col style={colW} />
                <col style={colW} />
                <col style={{ width: LIST_ACTIONS_COL_PX }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell sx={colW}>Name</TableCell>
                  {showCategory && <TableCell sx={colW}>Category</TableCell>}
                  <TableCell sx={colW}>HSN</TableCell>
                  <TableCell sx={colW}>Tax</TableCell>
                  <TableCell sx={colW}>Qty</TableCell>
                  <TableCell className="col-numeric" sx={colW}>
                    MRP
                  </TableCell>
                  <TableCell className="col-numeric" sx={colW}>
                    Purchase
                  </TableCell>
                  <TableCell className="col-retailer" sx={colW}>
                    Retailer
                  </TableCell>
                  <TableCell className="col-actions">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productsToRender.map((product) => {
                  const isInactive = String(product.status || 'ACTIVE').toUpperCase() === 'INACTIVE';
                  const productCode = product.sku || product.code || product.barcode;
                  const retailerPrice =
                    product.sellingPrice ?? product.minSalePrice ?? product.retailerPrice;

                  return (
                    <TableRow key={product.id} hover sx={{ opacity: isInactive ? 0.65 : 1 }}>
                      <TableCell sx={colW} title={product.name}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          noWrap
                          sx={{ color: isInactive ? 'text.disabled' : 'text.primary' }}
                        >
                          {product.name}
                        </Typography>
                        {productCode && (
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {productCode}
                          </Typography>
                        )}
                      </TableCell>
                      {showCategory && (
                        <TableCell
                          sx={colW}
                          title={product.category?.name || product.categoryName || 'Uncategorized'}
                        >
                          {product.category?.name || product.categoryName || 'Uncategorized'}
                        </TableCell>
                      )}
                      <TableCell sx={colW}>{formatHsn(product.hsnCode)}</TableCell>
                      <TableCell sx={colW}>{formatTax(product)}</TableCell>
                      <TableCell sx={colW}>
                        {product.quantity ?? 0} {product.unit?.abbreviation || ''}
                      </TableCell>
                      <TableCell className="col-numeric" sx={colW}>
                        {formatPrice(product.mrp)}
                      </TableCell>
                      <TableCell className="col-numeric" sx={colW}>
                        {formatPrice(product.purchasePrice)}
                      </TableCell>
                      <TableCell className="col-retailer" sx={colW}>
                        {formatPrice(retailerPrice)}
                      </TableCell>
                      <TableCell className="col-actions">
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, product.id)}
                          size="small"
                          aria-label="Product actions"
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      );
    },
    [handleMenuOpen]
  );

  // Memoize the product/category grid
  const contentGrid = useMemo(() => {
    if (loading) {
      return (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      );
    }

    // If a category is selected
    if (currentCategory) {
      const gridItems = [];

      // Only add products if available
      if (filteredProducts.length > 0) {
        if (viewMode === 'grid') {
          filteredProducts.forEach(product => {
            gridItems.push(renderProductCard(product));
          });
        } else {
          gridItems.push(renderProductListTable(filteredProducts, { showCategory: false, tableKey: 'product-list' }));
        }
      }

      // If we have either subcategories or products, return them
      if (gridItems.length > 0) {
        return gridItems;
      }

      // No content found
      return (
        <Grid item xs={12}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No content found
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Add a product to this category to get started
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
            >
              Add Product
            </Button>
          </Paper>
        </Grid>
      );
    }

    // We're in the ALL tab - show products
    if (filteredProducts.length > 0) {
      const allProductsItems = [];

      if (viewMode === 'grid') {
        filteredProducts.forEach((product) => {
          allProductsItems.push(renderProductCard(product));
        });
        return allProductsItems;
      }

      allProductsItems.push(renderProductListTable(filteredProducts, { showCategory: true, tableKey: 'all-product-list' }));
      return allProductsItems;
    }

    // No products found
    return (
      <Grid item xs={12}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No products found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Add products to get started
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Add Product
          </Button>
        </Paper>
      </Grid>
    );
  }, [loading, currentCategory, categories, filteredProducts, viewMode, renderCategoryCard, renderProductCard, renderProductListTable, handleOpenDialog, handleMenuOpen]);

  // Fetch products based on tab selection or search
  const fetchTabProducts = useCallback(async (categoryId = null) => {
    setLoading(true);
    try {
      // Determine if user is a location user (same logic as POS)
      let user = JSON.parse(localStorage.getItem('user')) || {};
      let userLocationData = user?.location || null;
      let hasLocation = userLocationData && userLocationData.id;

      if (!hasLocation) {
        try {
          const axios = (await import('../../utils/axios')).default;
          const response = await axios.get('/auth/me');
          if (response.data && response.data.location) {
            user = response.data;
            userLocationData = response.data.location;
            hasLocation = userLocationData && userLocationData.id;
            localStorage.setItem('user', JSON.stringify(user));
          }
        } catch (fetchError) {
          console.warn('[ProductsPage] Could not fetch user location from backend:', fetchError);
        }
      }

      if (hasLocation) {
        console.log('[ProductsPage] Location user detected - fetching location products for location:', userLocationData.name);
        const locationProducts = await productService.getLocationProducts(categoryId || null);

        const allLocationProducts = (locationProducts || []).map(lp => ({
          id: lp.productId,
          name: lp.productName,
          sku: lp.productCode,
          code: lp.productCode,
          barcode: lp.productCode,
          category: lp.categoryName ? { id: null, name: lp.categoryName } : null,
          categoryName: lp.categoryName || 'Uncategorized',
          quantity: Number(lp.quantity) || 0, // Quantity from location_products table
          mrp: lp.mrp,
          sellingPrice: Number(lp.sellingPrice) || Number(lp.purchasePrice) || 0,
          purchasePrice: Number(lp.purchasePrice) || 0,
          minSalePrice: Number(lp.minSalePrice) || Number(lp.sellingPrice) || 0,
          imageUrl: lp.imageUrl,
          status: lp.status || 'ACTIVE',
          hsnCode: lp.hsnCode || '',
          taxRate: lp.taxRate,
          tax: lp.taxRate != null ? { rate: lp.taxRate } : lp.tax || null,
          unit: lp.unit ? { abbreviation: lp.unit } : { abbreviation: 'PCS' },
          description: lp.description || '',
          locationProductId: lp.id,
          locationQuantity: Number(lp.quantity) || 0,
          locationId: lp.locationId,
          locationName: lp.locationName
        }));

        const startIndex = pagination.page * pagination.size;
        const endIndex = startIndex + pagination.size;
        const paginatedProducts = allLocationProducts.slice(startIndex, endIndex);

        setProducts(paginatedProducts);
        setFilteredProducts(paginatedProducts);
        setCatalogStats(computeCatalogStats(allLocationProducts, allLocationProducts.length));
        setPagination(prev => ({
          ...prev,
          totalPages: Math.max(1, Math.ceil(allLocationProducts.length / pagination.size)),
          totalElements: allLocationProducts.length
        }));

        return;
      }

      // Non-location users (Warehouse/Admin)
      let data;
      if (categoryId) {
        data = await productService.getProductsByCategoryForManagement(
          categoryId,
          pagination.page,
          pagination.size,
          'name',
          'asc'
        );
      } else {
        try {
          data = await productService.getAllProductsForManagement(
            pagination.page,
            pagination.size,
            'name',
            'asc'
          );
        } catch (err) {
          console.error('Error with paginated products, trying alternative method:', err);
          try {
            const allProductsResponse = await axios.get(
              `${API_CONFIG.BASE_URL}/products/all`,
              { headers: getAuthHeader() }
            );
            data = allProductsResponse.data;
          } catch (fallbackErr) {
            console.error('Both product fetch methods failed, using mock data:', fallbackErr);
            data = MOCK_PRODUCTS;
          }
        }
      }

      const applyStatsFromApi = async (apiData) => {
        const items = apiData?.content || [];
        const total = apiData?.totalElements ?? items.length;
        const totalPages = apiData?.totalPages || 1;

        if (totalPages <= 1 || items.length >= total) {
          setCatalogStats(computeCatalogStats(items, total));
          return;
        }

        setCatalogStats({ totalProducts: total, activeProducts: 0, totalStock: 0 });
        try {
          const statsFetch = categoryId
            ? productService.getProductsByCategoryForManagement(categoryId, 0, Math.min(total, 1000), 'name', 'asc')
            : productService.getAllProductsForManagement(0, Math.min(total, 1000), 'name', 'asc');
          const statsData = await statsFetch;
          setCatalogStats(computeCatalogStats(statsData?.content || [], total));
        } catch {
          setCatalogStats((prev) => ({ ...prev, totalProducts: total }));
        }
      };

      if (data && data.content) {
        setProducts(data.content);
        setFilteredProducts(data.content);
        setPagination(prev => ({
          ...prev,
          totalPages: data.totalPages || 1,
          totalElements: data.totalElements || data.content.length
        }));
        await applyStatsFromApi(data);
      } else if (Array.isArray(data)) {
        setProducts(data);
        setFilteredProducts(data);
        setCatalogStats(computeCatalogStats(data, data.length));
        setPagination(prev => ({
          ...prev,
          totalPages: 1,
          totalElements: data.length
        }));
      } else {
        console.warn('Unexpected data format:', data);
        setProducts([]);
        setFilteredProducts([]);
        setCatalogStats({ totalProducts: 0, activeProducts: 0, totalStock: 0 });
        setPagination(prev => ({
          ...prev,
          totalPages: 1,
          totalElements: 0
        }));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      enqueueSnackbar(`Failed to load products: ${error.message || 'Unknown error'}`, { variant: 'error' });

      // Last resort: Use mock data even after error
      console.log('Error occurred, using mock data');
      setProducts(MOCK_PRODUCTS);
      setFilteredProducts(MOCK_PRODUCTS);
      setCatalogStats(computeCatalogStats(MOCK_PRODUCTS, MOCK_PRODUCTS.length));
      setPagination(prev => ({
        ...prev,
        totalPages: 1,
        totalElements: MOCK_PRODUCTS.length
      }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, enqueueSnackbar]);

  // Handle tab change 
  const handleTabChange = (event, newValue) => {
    setActiveTabIndex(newValue);

    if (newValue === 0) {
      // "ALL" tab selected - show all products, not categories
      setCurrentCategory(null);
      fetchTabProducts(); // This will fetch all products
    } else if (mainCategories.length > 0) {
      // Set current category to the selected tab's category
      const selectedCategory = mainCategories[newValue - 1]; // -1 because "ALL" is index 0

      // Set current category regardless of whether it has subcategories
      setCurrentCategory(selectedCategory);

      // Always fetch products for this category
      fetchTabProducts(selectedCategory.id);
    }

    // Reset pagination
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  // Load products when a tab is selected, page changes, or component mounts
  useEffect(() => {
    // Ensure the API is available before trying to fetch
    if (!productService || !productService.getAllProductsForManagement) {
      console.error('Product service or getAllProductsForManagement method not available');
      enqueueSnackbar('Product service unavailable', { variant: 'error' });
      return;
    }

    // Initial data load
    if (activeTabIndex === 0) {
      // Load all products
      fetchTabProducts();
    } else if (currentCategory) {
      // Load category products
      fetchTabProducts(currentCategory.id);
    }
  }, [activeTabIndex, currentCategory, pagination.page, pagination.size]);

  const handleSearch = async (e) => {
    e.preventDefault(); // Prevent default form submission

    // Reset pagination for new search or clear
    setPagination(prev => ({ ...prev, page: 0 }));

    setLoading(true);
    try {
      if (searchQuery.trim()) {
        console.log(`Searching for "${searchQuery}" in category: ${currentCategory?.id || 'ALL'}`);
        // Perform server-side search
        const results = await productService.searchProductsForManagement(searchQuery);

        console.log('Search results:', results); // Debug log

        // Update state with search results
        // Assuming the search service returns a paginated structure { content: [], totalPages: X, ... }
        if (results && results.content) {
          setProducts(results.content);
          setFilteredProducts(results.content);
          setCatalogStats(computeCatalogStats(results.content, results.totalElements || results.content.length));
          setPagination(prev => ({
            ...prev,
            page: 0,
            totalPages: results.totalPages || 1,
            totalElements: results.totalElements || results.content.length
          }));
        } else if (Array.isArray(results)) {
          setProducts(results);
          setFilteredProducts(results);
          setCatalogStats(computeCatalogStats(results, results.length));
          setPagination(prev => ({
            ...prev,
            page: 0,
            totalPages: 1,
            totalElements: results.length
          }));
        } else {
          setProducts([]);
          setFilteredProducts([]);
          setCatalogStats({ totalProducts: 0, activeProducts: 0, totalStock: 0 });
          setPagination(prev => ({ ...prev, page: 0, totalPages: 0, totalElements: 0 }));
          enqueueSnackbar('No products found matching your search.', { variant: 'info' });
        }

      } else {
        // Clear search - revert to original data for the current view by refetching
        console.log(`Search cleared, fetching products for category: ${currentCategory?.id || 'ALL'}`);
        fetchTabProducts(currentCategory?.id); // Refetch all or category products
      }
    } catch (error) {
      console.error('Error searching:', error);
      enqueueSnackbar(`Search failed: ${error.message || 'Unknown error'}`, { variant: 'error' });
      // Clear results on error
      setProducts([]);
      setFilteredProducts([]);
      setPagination(prev => ({ ...prev, page: 0, totalPages: 0, totalElements: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (productId) => {
    const product = products.find(p => p.id === productId);
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const handleDeleteProduct = async () => {
    try {
      if (!productToDelete) return;
      await productService.deleteProduct(productToDelete.id);
      enqueueSnackbar('Product removed from your catalog', { variant: 'success' });
      handleCloseDeleteDialog();
      fetchTabProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      enqueueSnackbar('Failed to delete product', { variant: 'error' });
    }
  };

  // Handle view mode change — list view loads more rows per page
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null && newMode !== viewMode) {
      const newSize = newMode === 'list' ? LIST_PAGE_SIZE : GRID_PAGE_SIZE;
      setViewMode(newMode);
      setPagination((prev) => ({ ...prev, page: 0, size: newSize }));
    }
  };

  const paginationSummary = useMemo(() => {
    if (!pagination.totalElements) return '';
    const from = pagination.page * pagination.size + 1;
    const to = Math.min((pagination.page + 1) * pagination.size, pagination.totalElements);
    return `Showing ${from}–${to} of ${pagination.totalElements} products`;
  }, [pagination.page, pagination.size, pagination.totalElements]);

  // Handle category dropdown open
  const handleCategoryDropdownOpen = (event) => {
    setCategoryDropdownAnchorEl(event.currentTarget);
    setCategoryDropdownOpen(true);
  };

  // Handle category dropdown close
  const handleCategoryDropdownClose = () => {
    setCategoryDropdownAnchorEl(null);
    setCategoryDropdownOpen(false);
  };

  // Handle category selection from dropdown
  const handleCategorySelect = (categoryId) => {
    const categoryIndex = mainCategories.findIndex(cat => cat.id === categoryId);
    if (categoryIndex !== -1) {
      handleTabChange(null, categoryIndex + 1); // +1 because ALL is index 0
    }
    handleCategoryDropdownClose();
  };

  // Calculate max visible tabs based on screen width (will be used to determine when to show dropdown)
  const maxVisibleTabs = 8; // Adjust based on testing

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      const allToExport = [];

      // Detect location user same as fetchTabProducts
      let user = JSON.parse(localStorage.getItem('user')) || {};
      let userLocationData = user?.location || null;
      let hasLocation = userLocationData && userLocationData.id;

      if (!hasLocation) {
        try {
          const axios = (await import('../../utils/axios')).default;
          const response = await axios.get('/auth/me');
          if (response.data && response.data.location) {
            user = response.data;
            userLocationData = response.data.location;
            hasLocation = userLocationData && userLocationData.id;
            localStorage.setItem('user', JSON.stringify(user));
          }
        } catch (fetchError) {
          console.warn('[ProductsPage] Could not fetch user location from backend for export:', fetchError);
        }
      }

      if (hasLocation) {
        // Export all location products (optionally filtered by currentCategory)
        const locationProducts = await productService.getLocationProducts(currentCategory?.id || null);
        locationProducts.forEach(lp => {
          allToExport.push({
            'Product Name': lp.productName,
            'SKU ID': lp.sku || '',
            'Category': lp.categoryName || 'Uncategorized',
            'Quantity': Number(lp.quantity) || 0,
            'Selling Price': Number(lp.sellingPrice) || Number(lp.purchasePrice) || 0,
            'Purchase Price': Number(lp.purchasePrice) || 0,
            'Wholesale Rate': lp.wholesalePrice != null ? Number(lp.wholesalePrice) : '',
            'MRP': lp.mrp || '',
            'GST': lp.taxRate != null ? `${Number(lp.taxRate)}%` : '',
            'HSN Code': lp.hsnCode || '',
            'Status': lp.status || 'ACTIVE',
            'Unit': lp.unit || 'PCS'
          });
        });
      } else {
        // Warehouse/Admin: fetch all pages to export complete dataset
        const pageSize = 1000;
        let pageIndex = 0;
        let totalPages = 1;

        if (currentCategory?.id) {
          // Category-specific export
          do {
            const resp = await productService.getProductsByCategoryForManagement(
              currentCategory.id, pageIndex, pageSize, 'name', 'asc'
            );
            const content = resp?.content || (Array.isArray(resp) ? resp : []);
            content.forEach(product => {
              allToExport.push({
                'Product Name': product.name,
                'SKU ID': product.sku || '',
                'Category': product.category?.name || product.categoryName || 'Uncategorized',
                'Quantity': product.quantity || 0,
                'Selling Price': product.sellingPrice || 0,
                'Purchase Price': product.purchasePrice || 0,
                'Wholesale Rate': product.wholesalePrice != null ? Number(product.wholesalePrice) : '',
                'MRP': product.mrp || '',
                'GST': product.tax?.rate != null ? `${Number(product.tax.rate)}%` : '',
                'HSN Code': product.hsnCode || '',
                'Status': product.status || 'ACTIVE',
                'Unit': product.unit?.abbreviation || product.unit || 'PCS'
              });
            });
            totalPages = resp?.totalPages ?? (content.length < pageSize ? pageIndex + 1 : pageIndex + 2);
            pageIndex++;
          } while (pageIndex < totalPages);
        } else {
          // ALL products export
          do {
            const resp = await productService.getAllProductsForManagement(
              pageIndex, pageSize, 'name', 'asc'
            );
            const content = resp?.content || (Array.isArray(resp) ? resp : []);
            content.forEach(product => {
              allToExport.push({
                'Product Name': product.name,
                'SKU ID': product.sku || '',
                'Category': product.category?.name || product.categoryName || 'Uncategorized',
                'Quantity': product.quantity || 0,
                'Selling Price': product.sellingPrice || 0,
                'Purchase Price': product.purchasePrice || 0,
                'Wholesale Rate': product.wholesalePrice != null ? Number(product.wholesalePrice) : '',
                'MRP': product.mrp || '',
                'GST': product.tax?.rate != null ? `${Number(product.tax.rate)}%` : '',
                'HSN Code': product.hsnCode || '',
                'Status': product.status || 'ACTIVE',
                'Unit': product.unit?.abbreviation || product.unit || 'PCS'
              });
            });
            totalPages = resp?.totalPages ?? (content.length < pageSize ? pageIndex + 1 : pageIndex + 2);
            pageIndex++;
          } while (pageIndex < totalPages);
        }
      }

      const productsToExport = allToExport;

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(productsToExport);

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Products');

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Create download link
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `products_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Products exported successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting products:', error);
      enqueueSnackbar('Failed to export products', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ px: { xs: 1.5, sm: 2, md: 3 }, py: 1.5, bgcolor: '#fafbfc', minHeight: '100%' }}>
        {/* Summary cards */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={6} md={3}>
            <ProductStatCard
              label="Total Products"
              value={catalogStats.totalProducts.toLocaleString()}
              icon={InventoryIcon}
              color="#154c79"
              bgColor="#e8f0f8"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <ProductStatCard
              label="Categories"
              value={mainCategories.length.toLocaleString()}
              icon={CategoryIcon}
              color="#2e7d32"
              bgColor="#e8f5e9"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <ProductStatCard
              label="Total Stock"
              value={catalogStats.totalStock.toLocaleString()}
              suffix="units"
              icon={FolderOpenIcon}
              color="#e65100"
              bgColor="#fff3e0"
            />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <ProductStatCard
              label="Active Products"
              value={catalogStats.activeProducts.toLocaleString()}
              icon={CheckCircleIcon}
              color="#6a1b9a"
              bgColor="#f3e5f5"
            />
          </Grid>
        </Grid>

        {/* Category tabs — quick filter */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            mb: 1.5,
          }}
        >
          <Tabs
            value={activeTabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="product categories tabs"
            sx={{
              flexGrow: 1,
              minHeight: 40,
              '& .MuiTab-root': {
                minHeight: 40,
                textTransform: 'none',
              },
              '& .MuiTabs-scrollButtons': {
                '&.Mui-disabled': { opacity: 0.3 },
                '&:not(.Mui-disabled)': {
                  opacity: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: '50%',
                },
              },
            }}
          >
            <Tab
              label="All"
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                minWidth: 'auto',
                px: 1.5,
              }}
            />
            {mainCategories.slice(0, maxVisibleTabs).map((category) => (
              <Tab
                key={category.id}
                label={category.name}
                sx={{
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  minWidth: 'auto',
                  px: 1.5,
                  maxWidth: 160,
                }}
              />
            ))}
          </Tabs>

          {mainCategories.length > maxVisibleTabs && (
            <Button
              variant="text"
              color="primary"
              endIcon={<ExpandMoreIcon />}
              onClick={handleCategoryDropdownOpen}
              size="small"
              sx={{ ml: 0.5, flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              More
            </Button>
          )}

          {/* Dropdown menu for categories */}
          <Menu
            anchorEl={categoryDropdownAnchorEl}
            open={categoryDropdownOpen}
            onClose={handleCategoryDropdownClose}
            sx={{ maxHeight: 400 }}
            PaperProps={{
              sx: { maxHeight: 400, width: '250px' }
            }}
          >
            {/* All Categories option */}
            <MenuItem
              onClick={() => {
                setCurrentCategory(null);
                handleTabChange(null, 0); // 0 is "ALL"
                handleCategoryDropdownClose();
              }}
              selected={activeTabIndex === 0}
            >
              <CategoryIcon sx={{ mr: 1, fontSize: '1rem' }} />
              All Categories
            </MenuItem>
            <Divider />
            {/* All main categories */}
            {mainCategories.map((category) => (
              <MenuItem
                key={category.id}
                onClick={() => handleCategorySelect(category.id)}
                selected={activeTabIndex === mainCategories.findIndex(cat => cat.id === category.id) + 1}
              >
                <CategoryIcon sx={{ mr: 1, fontSize: '1rem' }} />
                {category.name}
                {category.code && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{category.code}</Typography>}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Toolbar: search, category jump, actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            <Box
              component="form"
              onSubmit={handleSearch}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
            >
              <TextField
                size="small"
                placeholder="Search by name, code, barcode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch(e);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                sx={{ minWidth: { xs: '100%', sm: 260 } }}
              />
              <Button type="submit" variant="contained" size="small" disabled={loading} onClick={handleSearch}>
                Search
              </Button>
            </Box>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select
                displayEmpty
                value={currentCategory?.id ?? ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    handleTabChange(null, 0);
                  } else {
                    const categoryId = typeof mainCategories[0]?.id === 'number' ? Number(val) : val;
                    handleCategorySelect(categoryId);
                  }
                }}
                renderValue={() => currentCategory?.name || 'All categories'}
                sx={{ bgcolor: 'background.paper', fontSize: '0.875rem' }}
              >
                <MenuItem value="">
                  <em>All categories</em>
                </MenuItem>
                {mainCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {currentCategory && (
              <Chip
                label={currentCategory.name}
                size="small"
                onDelete={() => handleTabChange(null, 0)}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog('create')}>
              Add Product
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={isExporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
              onClick={handleExportToExcel}
              disabled={isExporting || products.length === 0}
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view mode"
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '& .MuiToggleButton-root.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                },
              }}
            >
              <ToggleButton value="grid" aria-label="grid view">
                <GridViewIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list" aria-label="list view">
                <ListViewIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Products Display */}
        <Grid container spacing={viewMode === 'list' ? 0.5 : 2}>
          {contentGrid}
        </Grid>

        {/* Pagination */}
        {filteredProducts.length > 0 && pagination.totalPages > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mt: 2,
              mb: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {paginationSummary}
            </Typography>
            <Pagination
              count={pagination.totalPages}
              page={pagination.page + 1}
              onChange={handlePageChange}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
              disabled={loading}
            />
          </Box>
        )}

        {/* Product Creation/Editing Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: '90vh',
              height: '90vh',
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          <DialogContent sx={{ p: 0, overflow: 'auto', flex: 1 }}>
            <ProductForm
              initialData={currentProduct}
              onSubmit={handleProductSubmit}
              onCancel={handleCloseDialog}
              isViewMode={dialogMode === 'view'}
              focusSession={openDialog ? productDialogFocusSession : undefined}
              categoriesProp={allCategoriesForForm}
              unitsProp={allUnitsForForm}
              taxesProp={allTaxesForForm}
            />
          </DialogContent>
          {/* Add Close button for view mode */}
          {dialogMode === 'view' && (
            <DialogActions sx={{ px: 3, pb: 3, flexShrink: 0 }}>
              <Button
                variant="contained"
                onClick={handleCloseDialog}
              >
                Close
              </Button>
            </DialogActions>
          )}
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              This will remove the product from your product list and POS (the record stays in the database for past bills).
              You can reuse the same SKU or barcode on a new product. Continue?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button
              onClick={handleDeleteProduct}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={limitDialogOpen} onClose={() => setLimitDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Limit Reached</DialogTitle>
          <DialogContent>
            <Typography>{limitDialogMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLimitDialogOpen(false)}>OK</Button>
          </DialogActions>
        </Dialog>

        {/* Menu for product actions */}
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            handleEditProduct(selectedProductId, 'view');
            handleMenuClose();
          }}>
            <InventoryIcon sx={{ mr: 1 }} fontSize="small" />
            View Details
          </MenuItem>
          <MenuItem onClick={() => {
            handleEditProduct(selectedProductId, 'edit');
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
          <MenuItem onClick={() => {
            handleDeleteClick(selectedProductId);
            handleMenuClose();
          }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        </Menu>
      </Box>
    </Layout>
  );
};

export default ProductsPage; 
